<template>
  <div class="manager-stats-charts">
    <!-- Stats Cards Row -->
    <div class="manager-stats-grid">
      <div class="manager-stat-card">
        <div class="manager-stat-badge manager-stat-badge-blue">
          <i class="bi bi-people"></i>
        </div>
        <div class="manager-stat-content">
          <h3 class="manager-stat-value">{{ stats.totalClients }}</h3>
          <p class="manager-stat-label">Total Clients</p>
        </div>
      </div>
      <div class="manager-stat-card">
        <div class="manager-stat-badge manager-stat-badge-green">
          <i class="bi bi-person-check"></i>
        </div>
        <div class="manager-stat-content">
          <h3 class="manager-stat-value">{{ stats.activeClients }}</h3>
          <p class="manager-stat-label">Active Clients</p>
        </div>
      </div>
      <div class="manager-stat-card">
        <div class="manager-stat-badge manager-stat-badge-purple">
          <i class="bi bi-building"></i>
        </div>
        <div class="manager-stat-content">
          <h3 class="manager-stat-value">{{ stats.totalCompanies }}</h3>
          <p class="manager-stat-label">Total Companies</p>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="manager-charts-grid">
      <!-- Clients Status Chart -->
      <div class="manager-chart-card">
        <div class="manager-chart-header">
          <h4 class="manager-chart-title">
            <i class="bi bi-people"></i>
            Clients Status
          </h4>
        </div>
        <div class="manager-chart-body">
          <div class="manager-donut-chart">
            <svg class="manager-donut-chart-svg" viewBox="0 0 200 200">
              <circle
                class="manager-donut-chart-background"
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="#e2e8f0"
                stroke-width="20"
              />
              <circle
                class="manager-donut-chart-segment manager-donut-chart-active"
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="#48bb78"
                stroke-width="20"
                :stroke-dasharray="activeClientsCircumference + ' ' + totalCircumference"
                :stroke-dashoffset="0"
                transform="rotate(-90 100 100)"
              />
              <circle
                v-if="inactiveClients > 0"
                class="manager-donut-chart-segment manager-donut-chart-inactive"
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="#fed7d7"
                stroke-width="20"
                :stroke-dasharray="inactiveClientsCircumference + ' ' + totalCircumference"
                :stroke-dashoffset="-(activeClientsCircumference)"
                transform="rotate(-90 100 100)"
              />
            </svg>
            <div class="manager-donut-chart-center">
              <div class="manager-donut-chart-value">{{ stats.totalClients }}</div>
              <div class="manager-donut-chart-label">Total</div>
            </div>
          </div>
          <div class="manager-chart-legend">
            <div class="manager-chart-legend-item">
              <span class="manager-chart-legend-color manager-chart-legend-active"></span>
              <span class="manager-chart-legend-text">Active: {{ stats.activeClients }}</span>
            </div>
            <div class="manager-chart-legend-item">
              <span class="manager-chart-legend-color manager-chart-legend-inactive"></span>
              <span class="manager-chart-legend-text">Inactive: {{ inactiveClients }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Distribution Bar Chart -->
      <div class="manager-chart-card manager-chart-card-wide">
        <div class="manager-chart-header">
          <h4 class="manager-chart-title">
            <i class="bi bi-bar-chart"></i>
            Overview Distribution
          </h4>
        </div>
        <div class="manager-chart-body">
          <div class="manager-bar-chart">
            <div class="manager-bar-chart-item">
              <div class="manager-bar-chart-label">
                <i class="bi bi-people"></i>
                Total Clients
              </div>
              <div class="manager-bar-chart-bar-container">
                <div 
                  class="manager-bar-chart-bar manager-bar-chart-bar-blue"
                  :style="{ width: totalClientsPercentage + '%' }"
                >
                  <span class="manager-bar-chart-value">{{ stats.totalClients }}</span>
                </div>
              </div>
            </div>
            <div class="manager-bar-chart-item">
              <div class="manager-bar-chart-label">
                <i class="bi bi-person-check"></i>
                Active Clients
              </div>
              <div class="manager-bar-chart-bar-container">
                <div 
                  class="manager-bar-chart-bar manager-bar-chart-bar-green"
                  :style="{ width: activeClientsPercentage + '%' }"
                >
                  <span class="manager-bar-chart-value">{{ stats.activeClients }}</span>
                </div>
              </div>
            </div>
            <div class="manager-bar-chart-item">
              <div class="manager-bar-chart-label">
                <i class="bi bi-building"></i>
                Total Companies
              </div>
              <div class="manager-bar-chart-bar-container">
                <div 
                  class="manager-bar-chart-bar manager-bar-chart-bar-purple"
                  :style="{ width: totalCompaniesPercentage + '%' }"
                >
                  <span class="manager-bar-chart-value">{{ stats.totalCompanies }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/ManagerStatsCharts.css';

export default {
  name: 'ManagerStatsCharts',
  props: {
    stats: {
      type: Object,
      required: true,
      default: () => ({
        totalClients: 0,
        activeClients: 0,
        totalCompanies: 0
      })
    }
  },
  computed: {
    inactiveClients() {
      return this.stats.totalClients - this.stats.activeClients;
    },
    totalCircumference() {
      return 2 * Math.PI * 80; // radius is 80
    },
    activeClientsCircumference() {
      if (this.stats.totalClients === 0) return 0;
      const percentage = this.stats.activeClients / this.stats.totalClients;
      return this.totalCircumference * percentage;
    },
    inactiveClientsCircumference() {
      if (this.stats.totalClients === 0) return 0;
      const percentage = this.inactiveClients / this.stats.totalClients;
      return this.totalCircumference * percentage;
    },
    maxValue() {
      return Math.max(
        this.stats.totalClients,
        this.stats.activeClients,
        this.stats.totalCompanies,
        1
      );
    },
    totalClientsPercentage() {
      return (this.stats.totalClients / this.maxValue) * 100;
    },
    activeClientsPercentage() {
      return (this.stats.activeClients / this.maxValue) * 100;
    },
    totalCompaniesPercentage() {
      return (this.stats.totalCompanies / this.maxValue) * 100;
    }
  }
}
</script>
